import Button from "common/components/Button/Button";

import { Enclosure } from "types/Enclosure";
import { Link } from "react-router-dom";
import useAuth from "common/hooks/useAuth";
import { useTranslation } from "react-i18next";
import { IoPawOutline } from "react-icons/io5";
import { PiTreePalmLight } from "react-icons/pi";
import AnimalListOverview from "components/Item/AnimalListOverview";
import { useMemo } from "react";
import _ from "lodash";
import ObservationGroupListOverview from "components/Item/ObservationGroupListOverview";
import TooltipWrapper from "common/components/TooltipWrapper/TooltipWrapper";

function EnclosureItem({ item, onDelete, onNavigate }: any) {
  const { user: currentUser } = useAuth();
  const canSeeDetailsFromItem =
    (currentUser?.isSuperadmin ||
      (currentUser?.isAdmin &&
        Boolean(currentUser?.park.id === item.park.id)) ||
      ((currentUser?.isManager || currentUser?.isObserver) &&
        Boolean(currentUser?.park.id === item.park.id) &&
        currentUser?.administrationPermissions?.enclosure?.read)) ??
    false;
  const canEditItem =
    (currentUser?.isSuperadmin ||
      (currentUser?.isAdmin &&
        Boolean(currentUser?.park.id === item.park.id)) ||
      ((currentUser?.isManager || currentUser?.isObserver) &&
        Boolean(currentUser?.park.id === item.park.id) &&
        currentUser?.administrationPermissions?.enclosure?.update)) ??
    false;
  const canDeleteItem =
    (currentUser?.isSuperadmin ||
      (currentUser?.isAdmin &&
        Boolean(currentUser?.park.id === item.park.id)) ||
      ((currentUser?.isManager || currentUser?.isObserver) &&
        Boolean(currentUser?.park.id === item.park.id) &&
        currentUser?.administrationPermissions?.enclosure?.delete)) ??
    false;

  const { name, description, animals, sector, observationGroups } =
    item as Enclosure;

  const observationGroupsAnimals = useMemo(() => {
    return Array.from(
      new Map(
        observationGroups
          .flatMap((outer) => outer.animals)
          .map((animal) => [animal?.id, animal])
      ).values()
    );
  }, [observationGroups]);

  const uniqAnimals = useMemo(() => {
    const items = [...(animals ?? []), ...(observationGroupsAnimals ?? [])];
    const itemsIds = _.uniq(items?.filter((i) => i).map((i) => i?.id));
    return itemsIds?.map((i) => items.find((a) => a.id === i));
  }, [animals, observationGroupsAnimals]);

  const editLink = `/enclosures/edit/${item.id}`;

  const { t } = useTranslation();

  return (
    <div className="flex px-4 py-4 mt-2 w-full relative bg-white cursor-pointer rounded-lg border border-slate-150 transition-all">
      <Link
        to={editLink}
        className={
          !canSeeDetailsFromItem
            ? "pointer-events-none flex flex-1"
            : "flex flex-1"
        }
      >
        <div className="flex flex-col flex-1 justify-between pl-4 pr-6 mb-2">
          <div className="flex mt-4 gap-3">
            <div className="flex-1">
              <h4 className="text-xl font-bold text-primary leading-4">
                {name}
              </h4>
              {sector && (
                <div className="flex items-center gap-1 text-sm mt-1 mb-2">
                  <PiTreePalmLight className="w-3 h-3" />
                  <span className="font-bold">{sector.name}</span>
                </div>
              )}
              <div className="flex items-center text-sm">
                <span className="font-bold">{description}</span>
              </div>
            </div>
            {Boolean(animals?.length) && (
              <AnimalListOverview animals={animals} />
            )}
            {Boolean(observationGroupsAnimals?.length) && (
              <ObservationGroupListOverview
                observationGroups={observationGroups}
                title={t("animals.animalsInGroup")}
              />
            )}
          </div>
        </div>
      </Link>

      <div className="flex flex-col justify-between items-end text-xs lg:border-l pl-4 lg:min-w-[22em] border-slate-150 gap-2">
        <div className="flex items-center gap-1 text-sm">
          <IoPawOutline className="w-3 h-3" />
          <span className="font-bold">
            {t("forms.animalsLength", {
              count: uniqAnimals?.reduce((accumulator, currentValue) => {
                return (
                  accumulator +
                  (currentValue?.isGroup
                    ? currentValue?.individualsNumber ?? 0
                    : 1)
                );
              }, 0),
            })}
          </span>
        </div>
        <div className="flex w-full items-center justify-end text-slate-500 gap-1">
          <TooltipWrapper content={<span>{t("actions.delete")}</span>}>
            <Button
              type="error-line"
              icon="TrashIcon"
              compact
              onClick={() => onDelete(item.id)}
              disabled={!canDeleteItem}
              confirmMessage={t("actions.confirmDelete")}
            />
          </TooltipWrapper>

          <Button
            title={t("actions.edit")}
            type="warning-line"
            icon="PencilSquareIcon"
            onClick={() => onNavigate(item.id)}
            disabled={!canEditItem}
          />
        </div>
      </div>
    </div>
  );
}

export default EnclosureItem;
